import { adminAutomationsService } from '@/services/admin-automations.service';
import type { AdminFilters } from '@/types/admin.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useAdminExecutions(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-executions', filters],
    queryFn: () => adminAutomationsService.getExecutions(filters),
  });
}

export function useAdminExecutionDetail(uuid: string | undefined) {
  return useQuery({
    queryKey: ['admin-execution', uuid],
    queryFn: () => adminAutomationsService.getExecutionById(uuid!),
    enabled: !!uuid,
  });
}

export function useRetryExecution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminAutomationsService.retryExecution(uuid),
    onSuccess: () => {
      toast.success('Relance en cours');
      qc.invalidateQueries({ queryKey: ['admin-executions'] });
    },
    onError: () => toast.error('Erreur lors de la relance'),
  });
}

export function useCancelExecution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminAutomationsService.cancelExecution(uuid),
    onSuccess: () => {
      toast.success('Exécution annulée');
      qc.invalidateQueries({ queryKey: ['admin-executions'] });
    },
    onError: () => toast.error("Erreur lors de l'annulation"),
  });
}

export function useAdminQueues() {
  return useQuery({
    queryKey: ['admin-queues'],
    queryFn: () => adminAutomationsService.getQueues(),
    refetchInterval: 10000,
  });
}

export function usePauseQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => adminAutomationsService.pauseQueue(name),
    onSuccess: () => {
      toast.success('File mise en pause');
      qc.invalidateQueries({ queryKey: ['admin-queues'] });
    },
    onError: () => toast.error('Erreur'),
  });
}

export function useResumeQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => adminAutomationsService.resumeQueue(name),
    onSuccess: () => {
      toast.success('File reprise');
      qc.invalidateQueries({ queryKey: ['admin-queues'] });
    },
    onError: () => toast.error('Erreur'),
  });
}

export function useAutomationStats(period: string = '7d') {
  return useQuery({
    queryKey: ['admin-automation-stats', period],
    queryFn: () => adminAutomationsService.getStats(period),
  });
}

export function useAutomationEffectAnomalies(limit = 10) {
  return useQuery({
    queryKey: ['admin-automation-effect-anomalies', limit],
    queryFn: () => adminAutomationsService.getEffectAnomalies(limit),
    refetchInterval: 60_000,
  });
}
